import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { IoArrowBack } from "react-icons/io5";
import { getToken } from "../../../../utils/authHelper";
import arService from "../../../../services/arService";
import "./CustomerLedgerView.css";

const CustomerLedgerView = () => {
  const { rowId } = useParams();
  const customerId = rowId;
  const navigate = useNavigate();

  const [customerInfo, setCustomerInfo] = useState(null);
  const [balanceSummary, setBalanceSummary] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (customerId) {
      loadCustomerLedger();
    }
  }, [customerId]);

  const loadCustomerLedger = async () => {
    setIsLoading(true);
    try {
      const token = getToken();
      const customerRes = await fetch(
        `${process.env.REACT_APP_NODE_SERVICE_BASE_URL}/customer/api/customers/${customerId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (customerRes.ok) {
        setCustomerInfo(await customerRes.json());
      }

      const [balance, invoiceList] = await Promise.all([
        arService.getCustomerBalance(customerId),
        arService.getCustomerInvoicePayments(customerId),
      ]);

      setBalanceSummary(balance);
      setInvoices(Array.isArray(invoiceList) ? invoiceList : []);
    } catch (error) {
      console.error("Error loading customer ledger:", error);
      setBalanceSummary(null);
      setInvoices([]);
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2,
    }).format(amount || 0);

  const formatDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    return Number.isNaN(date.getTime())
      ? value
      : date.toLocaleDateString("en-GB");
  };

  const getStatusClass = (status) => {
    const normalized = (status || "").toLowerCase().replace(/\s+/g, "-");
    if (normalized === "paid") return "status-paid";
    if (normalized === "partially-paid") return "status-partial";
    return "status-unpaid";
  };

  const invoiceStats = invoices.reduce(
    (acc, inv) => {
      const status = (inv.payment_status || "Unpaid").toLowerCase();
      if (status === "paid") acc.paid += 1;
      else if (status.includes("partial")) acc.partial += 1;
      else acc.unpaid += 1;
      return acc;
    },
    { paid: 0, partial: 0, unpaid: 0 }
  );

  return (
    <div className="customer-ledger-view">
      <div className="ledger-header">
        <div className="header-left">
          <button className="back-button" onClick={() => navigate("/customers")}>
            <IoArrowBack /> Back to Customers
          </button>
          <h2>
            Customer Ledger - {customerInfo?.name || balanceSummary?.customer_name || `Customer ${customerId}`}
          </h2>
        </div>
      </div>

      {isLoading ? (
        <div className="loading-state">
          <p>Loading customer ledger...</p>
        </div>
      ) : (
        <>
          <div className="summary-cards">
            <div className="summary-card highlight">
              <h4>On Account</h4>
              <p className="on-account">
                {formatCurrency(balanceSummary?.on_account_balance || 0)}
              </p>
            </div>
            <div className="summary-card">
              <h4>Outstanding</h4>
              <p className="negative">
                {formatCurrency(balanceSummary?.total_outstanding || 0)}
              </p>
            </div>
            <div className="summary-card">
              <h4>Total Invoiced</h4>
              <p>{formatCurrency(balanceSummary?.total_invoiced || 0)}</p>
            </div>
            <div className="summary-card">
              <h4>Total Received</h4>
              <p className="positive">
                {formatCurrency(balanceSummary?.total_received || 0)}
              </p>
            </div>
            <div className="summary-card">
              <h4>Overdue</h4>
              <p className="negative">
                {formatCurrency(balanceSummary?.total_overdue || 0)}
              </p>
            </div>
          </div>

          <div className="invoice-status-summary">
            <span className="status-chip status-paid">{invoiceStats.paid} Paid</span>
            <span className="status-chip status-partial">{invoiceStats.partial} Partially Paid</span>
            <span className="status-chip status-unpaid">{invoiceStats.unpaid} Unpaid</span>
          </div>

          <div className="ledger-section">
            <h3>Invoices</h3>
            {invoices.length === 0 ? (
              <div className="no-data">
                <p>No approved invoices found for this customer.</p>
              </div>
            ) : (
              <div className="ledger-table-container">
                <table className="ledger-table">
                  <thead>
                    <tr>
                      <th>Invoice No</th>
                      <th>Date</th>
                      <th>Due Date</th>
                      <th>Amount</th>
                      <th>Paid</th>
                      <th>Pending</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((invoice) => (
                      <tr key={invoice.id}>
                        <td>{invoice.invoice_no || "-"}</td>
                        <td>{formatDate(invoice.invoice_date)}</td>
                        <td>{formatDate(invoice.due_date)}</td>
                        <td>{formatCurrency(invoice.invoice_total)}</td>
                        <td className="positive">
                          {formatCurrency(invoice.paid_amount)}
                        </td>
                        <td className="negative">
                          {formatCurrency(invoice.pending_amount)}
                        </td>
                        <td>
                          <span
                            className={`payment-status-badge ${getStatusClass(
                              invoice.payment_status
                            )}`}
                          >
                            {invoice.payment_status || "Unpaid"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default CustomerLedgerView;
